import { useState } from 'react';
import { resendVerificationEmail } from '../../api/auth.ts';
import { localizeApiError } from '../../api/errorMessages.ts';
import { useT } from '../../i18n/index.ts';
import { Button } from '../../ui/index.ts';

/**
 * Inline "send the verification email again" control. Shown when sign-in
 * is refused with EMAIL_NOT_VERIFIED. Success, failure, and rate limits
 * all stay on localized copy — the endpoint itself does not enumerate
 * whether the address matched.
 */
export function ResendVerification({
  email,
  fetchImpl,
}: {
  readonly email: string;
  readonly fetchImpl: typeof fetch;
}): JSX.Element {
  const t = useT();
  const [submitting, setSubmitting] = useState(false);
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleClick(): Promise<void> {
    setError(null);
    setAck(false);
    setSubmitting(true);
    try {
      await resendVerificationEmail({ email }, fetchImpl);
      setAck(true);
    } catch (err) {
      setError(localizeApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col items-start gap-2">
      {ack ? (
        <p className="m-0 text-caption text-ink-low" role="status" data-testid="login-resend-ack">
          {t('verify.resend.ack.body')}
        </p>
      ) : null}
      {error !== null ? (
        <p className="m-0 text-label text-bad" role="alert" data-testid="login-resend-error">
          {error}
        </p>
      ) : null}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        loading={submitting}
        onClick={() => {
          void handleClick();
        }}
        data-testid="login-resend-verification"
      >
        {submitting ? t('register.resend.submitting') : t('register.resend.submit')}
      </Button>
    </div>
  );
}
